import { InfoCircleOutlined } from '@ant-design/icons';
import { arrayMove } from '@dnd-kit/sortable';
import { Form, Layout, Tooltip } from 'antd';
import type { Location } from 'history';
import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { Mode } from 'app/components/commons/Hotel/Types';
import { BasicInfo } from 'app/components/commons/Package/General/BasicInfo/BasicInfo';
import { SortableExperiencesList } from 'app/components/commons/Package/General/ExperienceList/ExperienceList';
import { SortableRoomList } from 'app/components/commons/Package/General/RoomList/RoomList';
import type {
  ExperienceType,
  RoomType,
} from 'app/components/commons/Package/General/Types';
import { Spinner } from 'app/components/commons/Spinner';
import { SwitchableInput } from 'app/components/commons/SwitchableInput/SwitchableInput';
import { CustomInput } from 'app/components/fields/Input';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { useHotelExperiences } from 'app/hooks/data/hotels/useHotelExperience';
import { useGetPackage } from 'app/hooks/data/packages/usePackages';
import { useSavePackage } from 'app/hooks/data/packages/useSavePackage';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { useRooms } from 'app/hooks/data/useRooms';
import type { PackageForm } from 'app/typings/packages';
import { parseInputNumber } from 'app/utils/typing';

import { Discount } from './Discount/Discount';
import './General.scss';

const { Content } = Layout;

type FormValues = {
  id: number;
  hotelId: number;
  slug: string;
  dayPackage: boolean;
  channelManagerRateCode?: string | null;
  experiences: ExperienceType[];
  rooms: RoomType[];
  singleCustomerDiscount?: number;
  additionalAdultPrice?: number;
  additionalChildPrice?: number;
  childrenAllowed: boolean;
  minimumDiscountOverride: number;
  minimumDiscountOverrideDates: Date[];
};
type PaxOption =
  | 'additionalAdultPrice'
  | 'singleCustomerDiscount'
  | 'additionalChildPrice';

type Props = {
  headerMenu?: ReactNode;
  headerExtra: ReactNode[];
  packageId: number | null;
};

export const General = ({ headerMenu, headerExtra, packageId }: Props) => {
  const { state } = useLocation<Location>();

  const { data: pkg, isLoading: pkgLoading } = useGetPackage(packageId);

  const [hotelId, setHotelId] = useState<number>();

  const { data: hotels, isLoading: hotelsLoading } = useHotels();

  const { data: rooms, isLoading: roomsLoading } = useRooms(hotelId);
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: hotelExperiences, isLoading: expLoading } = useHotelExperiences(
    hotelId,
    { includeStaycationExp: true }
  );
  const {
    mutateAsync: savePackage,
    isLoading: savePackageLoading,
    isError,
  } = useSavePackage();
  const packageName = pkg?.aliasForHotel?.trim();

  const history = useHistory();
  const [mode, setMode] = useState<Mode>(packageId ? Mode.View : Mode.Edit);
  const [isDirtyMove, setIsDirtyMove] = useState(false);
  const [form] = Form.useForm();
  const { currencySymbol } = useContext(CurrencyContext);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hotelIdFromUrl = Number(searchParams.get('hotelId')) || undefined;
  const isDaytimeFromUrl = searchParams.get('isDaytime') === 'true';

  useEffect(() => {
    if (!pkgLoading && pkg) {
      setHotelId(pkg.hotelId);
    } else if (hotelIdFromUrl) {
      setHotelId(hotelIdFromUrl);
    }
  }, [pkg, pkgLoading, hotelIdFromUrl]);

  const onCancel = useCallback(() => {
    if (packageId) {
      setMode(Mode.View);
    } else {
      history.push('/packages');
    }
  }, [packageId, history]);

  const hotel = useMemo(() => {
    if (!hotelId) {
      return null;
    }

    return hotels?.find((h) => h.id === hotelId);
  }, [hotels, hotelId]);

  const formattedStaycationExperiences = useMemo(() => {
    if (!hotelId) {
      return [];
    }

    const filtered = hotelExperiences?.filter((exp) =>
      hotel ? exp.countryId === hotel.countryId : exp.hotelId !== hotelId
    );

    const parsed = filtered?.map((exp) => {
      const packageExperience = pkg?.experiences.find((e) => e.id === exp.id);

      return {
        ...packageExperience,
        ...exp,
        title: computeExperienceName({
          category: experienceCategories?.find(
            (cat) => cat.id === exp.categoryId
          ),
          ...exp,
        }),
        description: undefined,
        avatar: exp.coverPicture,
        included: packageExperience?.included ? 'Included' : 'Add-on',
        pictures: exp.pictures,
        id: exp.id,
      };
    });

    return parsed;
  }, [
    hotelExperiences,
    hotel,
    hotelId,
    pkg?.experiences,
    experienceCategories,
  ]);

  const formattedHotelExperiences = useMemo(() => {
    if (!hotelId) {
      return [];
    }

    const experiencesArray = hotelExperiences ?? [];
    const filtered = experiencesArray.filter((exp) => exp.hotelId === hotelId);

    const parsed = filtered?.map((exp) => {
      const packageExperience = pkg?.experiences.find((e) => e.id === exp.id);

      return {
        ...packageExperience,
        ...exp,
        title: computeExperienceName({
          category: experienceCategories?.find(
            (cat) => cat.id === exp.categoryId
          ),
          ...exp,
        }),
        description: undefined,
        avatar: exp.coverPicture,
        included: packageExperience?.included ? 'Included' : 'Add-on',
        pictures: exp.pictures,
        id: exp.id,
      };
    });

    return parsed;
  }, [pkg, experienceCategories, hotelExperiences, hotelId]);

  const formattedRooms = useMemo(
    () =>
      rooms?.map((room) => ({
        ...room,
        title: room.name || room.categoryName,
        description: room.name ? room.categoryName : undefined,
        avatar: room.pictures[0],
        id: room.id,
      })),
    [rooms]
  );

  const formattedValues = useMemo(
    () =>
      packageId && pkg && formattedStaycationExperiences
        ? {
            ...pkg,
            experiences: pkg.experiences.map((exp) => {
              const expFull = formattedHotelExperiences
                ?.concat(formattedStaycationExperiences)
                ?.find((e) => e.id === exp.id);

              return { ...exp, ...expFull };
            }),
            rooms: pkg.rooms
              .map((room) => formattedRooms?.find((r) => r.id === room))
              .filter((room) => !!room),
          }
        : {
            hotelId: hotelIdFromUrl,
            experiences: [],
            rooms: [],
            dayPackage: isDaytimeFromUrl,
            additionalAdultPrice: undefined,
            additionalChildPrice: undefined,
            singleCustomerDiscount: undefined,
            childrenAllowed: true,
          },
    [
      packageId,
      pkg,
      formattedStaycationExperiences,
      hotelIdFromUrl,
      isDaytimeFromUrl,
      formattedHotelExperiences,
      formattedRooms,
    ]
  );

  // We want to reset the form every time the formattedValues changes,
  // because it means one of the requests (getHotelExperiences, getHotelRooms... ) was resolved.
  // If we don't do it, the form will be filled with incomplete values.
  // To avoid this kind of tricks, we could start to render the form only after all the requests are resolved.
  useEffect(() => {
    if (packageId && formattedValues) {
      form.resetFields();
    }
  }, [packageId, formattedValues, form]);

  const checkIncluded = (values: FormValues) =>
    !values.experiences?.length ||
    values.experiences?.some(
      (exp: ExperienceType) => exp.included === 'Included'
    );

  const isLoading =
    hotelsLoading || (!!pkg && (pkgLoading || expLoading || roomsLoading));

  const paxOptionsObject = useMemo(
    () => ({
      additionalAdultPrice: {
        title: 'Fee for extra adult',
        value: formattedValues.additionalAdultPrice,
        name: 'additionalAdultPrice',
        wordingFirstPart: 'Apply an additional fee of',
        wordingSecondPart: 'per extra adult guest',
      },
      singleCustomerDiscount: {
        title: 'Single guest discount',
        value: formattedValues.singleCustomerDiscount,
        name: 'singleCustomerDiscount',
        wordingFirstPart: 'Apply a discount of',
        wordingSecondPart: 'for single guest',
      },
      additionalChildPrice: {
        title: 'Fee for extra child',
        value: formattedValues.additionalChildPrice,
        name: 'additionalChildPrice',
        wordingFirstPart: 'Apply an additional fee of',
        wordingSecondPart: 'per extra child guest',
      },
    }),
    [
      formattedValues.additionalAdultPrice,
      formattedValues.additionalChildPrice,
      formattedValues.singleCustomerDiscount,
    ]
  );

  const [paxOptionsSwitchValues, setPaxOptionsSwitchValues] = useState<
    | {
        additionalAdultPrice: boolean;
        singleCustomerDiscount: boolean;
        additionalChildPrice: boolean;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    setPaxOptionsSwitchValues({
      additionalAdultPrice: formattedValues.additionalAdultPrice !== undefined,
      singleCustomerDiscount:
        formattedValues.singleCustomerDiscount !== undefined,
      additionalChildPrice: formattedValues.additionalChildPrice !== undefined,
    });
  }, [formattedValues]);

  const renderPaxOptionInput = useCallback(
    (paxOption: PaxOption) => {
      const value =
        form.getFieldValue([paxOption]) ?? formattedValues[paxOption];

      return (
        <div
          className={mode !== Mode.View || !!value ? 'input-value' : 'no-value'}
        >
          {mode === Mode.View ? (
            value && (
              <div>
                {value}
                {currencySymbol}
              </div>
            )
          ) : (
            <>
              {paxOptionsObject?.[paxOption].wordingFirstPart}
              <Form.Item name={paxOption} noStyle>
                <CustomInput
                  disabled={!paxOptionsSwitchValues?.[paxOption]}
                  suffix={currencySymbol}
                  onWheel={(e) => e.currentTarget.blur()}
                  min={0}
                  parser={parseInputNumber}
                />
              </Form.Item>
            </>
          )}
          {(mode !== Mode.View || !!value) &&
            paxOptionsObject?.[paxOption].wordingSecondPart}
        </div>
      );
    },
    [
      form,
      formattedValues,
      mode,
      currencySymbol,
      paxOptionsObject,
      paxOptionsSwitchValues,
    ]
  );

  const renderSwitchableInput = useCallback(
    (
      paxOption:
        | 'additionalAdultPrice'
        | 'singleCustomerDiscount'
        | 'additionalChildPrice',
      disabled = false
    ) => {
      return (
        <div className="sectionSwitchContainer">
          <SwitchableInput
            disabled={mode === Mode.View || disabled}
            value={paxOptionsSwitchValues?.[paxOption] ?? false}
            onChange={() => {
              form.setFieldValue(paxOption, undefined);
              setIsDirtyMove(true);
              !!paxOptionsSwitchValues &&
                setPaxOptionsSwitchValues({
                  ...paxOptionsSwitchValues,
                  [paxOption]: !paxOptionsSwitchValues?.[paxOption],
                });
            }}
            childNode={renderPaxOptionInput(paxOption)}
            name={paxOptionsObject?.[paxOption].title}
            noBackground
            noBorder
          />
        </div>
      );
    },
    [mode, paxOptionsSwitchValues, renderPaxOptionInput, paxOptionsObject, form]
  );

  const onFinish = useCallback(
    async (values: FormValues) => {
      const formattedPkg = {
        ...values,
        experiences: values.experiences?.map(
          (
            exp: ExperienceType & {
              discover?: boolean;
              included?: string | boolean;
            }
          ) => ({
            ...exp,
            discover: exp.discover || false,
            included: exp.included === 'Included',
          })
        ),
        rooms: values.rooms.map((room) => room.id),
        id: packageId ? packageId : undefined,
        minimumDiscountOverride:
          values.minimumDiscountOverride !== null &&
          values.minimumDiscountOverride !== undefined
            ? values.minimumDiscountOverride / 100
            : undefined,
        minimumDiscountOverrideDates: values.minimumDiscountOverrideDates ?? [],
        channelManagerRateCode: values.dayPackage
          ? null
          : values.channelManagerRateCode,
      };

      const newPackageId = await savePackage(
        formattedPkg as unknown as PackageForm
      );

      history.push({
        pathname: `/packs/legacy/${newPackageId ?? packageId}`,
        state,
      });

      setMode(Mode.View);
    },
    [history, packageId, savePackage, state]
  );

  return (
    <FormLegacyRenderProp
      form={form}
      initialValues={formattedValues}
      onFinish={onFinish}
      className="general-form"
    >
      {(
        values,
        {
          resetFields,
          isFieldsTouched,
          submit,
          setFieldsValue,
          getFieldsError,
          setFieldValue,
        }
      ) => (
        <SDetailLayout
          title={
            <AssetDetailTitle
              assetTitle={
                packageId && pkg
                  ? packageName && packageName.length
                    ? packageName
                    : `Package #${packageId}`
                  : 'New Package'
              }
              hotelId={hotel?.id ?? hotelId}
              hotelName={hotel?.name}
              isNewDataModel={false}
            />
          }
          mode={mode}
          onEdit={() => setMode(Mode.Edit)}
          onSave={() => {
            submit();
            setIsDirtyMove(false);
          }}
          onCancel={() => {
            onCancel();
            setIsDirtyMove(false);
          }}
          reset={resetFields}
          isDirty={isDirtyMove || isFieldsTouched()}
          headerMenu={headerMenu}
          headerExtra={headerExtra}
          disabled={mode === Mode.Edit && !checkIncluded(values)}
          isValid={
            getFieldsError().every((item) => item.errors.length === 0) &&
            !isError
          }
          backRoute={state?.pathname}
          isLoading={savePackageLoading}
        >
          <Content className="package-detail__content">
            <Spinner spinning={isLoading}>
              <SCard title="Basic information">
                <BasicInfo
                  mode={mode}
                  values={values}
                  newHotel={!packageId}
                  setFieldValue={setFieldValue}
                  onHotelSelect={(id: number) => setHotelId(id)}
                />
              </SCard>
              {!!values.hotelId && !!hotelExperiences && !!rooms && (
                <>
                  <SCard title="Experiences">
                    <SortableExperiencesList
                      mode={mode}
                      selectedExperiences={values?.experiences}
                      hotelExperiences={formattedHotelExperiences}
                      staycationExperiences={formattedStaycationExperiences}
                      move={({ oldIndex, newIndex }) => {
                        setIsDirtyMove(true);
                        setFieldsValue({
                          experiences: arrayMove(
                            values.experiences,
                            oldIndex,
                            newIndex
                          ),
                        });
                      }}
                    />
                  </SCard>
                  <SCard title="Rooms">
                    <SortableRoomList
                      mode={mode}
                      rooms={values?.rooms}
                      allRooms={formattedRooms}
                      move={({ oldIndex, newIndex }) => {
                        setIsDirtyMove(true);
                        setFieldsValue({
                          rooms: arrayMove(values.rooms, oldIndex, newIndex),
                        });
                      }}
                    />
                  </SCard>
                  <SCard
                    title="PAX options"
                    subtitle="This fee/discount will be applied to the total price (package price + room price)"
                  >
                    <div className="pax-options-container">
                      <div className="adult-section">
                        <div className="section-title">
                          Adults
                          <Tooltip title={`Over 12 years old`}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </div>
                        <div className="section-switch">
                          {renderSwitchableInput('additionalAdultPrice')}
                          {renderSwitchableInput('singleCustomerDiscount')}
                        </div>
                      </div>
                      <div className="chidren-section">
                        <div className="section-title">
                          Children
                          <Tooltip title={`Between 2 and 12 years old`}>
                            <InfoCircleOutlined />
                          </Tooltip>
                        </div>

                        <div className="section-switch">
                          <div className="sectionSwitchContainer">
                            <Form.Item name={'childrenAllowed'}>
                              <SwitchableInput
                                disabled={mode === Mode.View}
                                value={
                                  form.getFieldValue('childrenAllowed') ?? false
                                }
                                childNode={<></>}
                                onChange={() => {
                                  form.setFieldValue(
                                    'additionalChildPrice',
                                    undefined
                                  );
                                  setIsDirtyMove(true);
                                  !!paxOptionsSwitchValues &&
                                    setPaxOptionsSwitchValues({
                                      ...paxOptionsSwitchValues,
                                      additionalChildPrice: false,
                                    });
                                }}
                                title={'Allow children'}
                                name="childrenAllowed"
                                noBackground
                                noBorder
                              />
                            </Form.Item>
                          </div>
                          {renderSwitchableInput(
                            'additionalChildPrice',
                            !form.getFieldValue('childrenAllowed')
                          )}
                        </div>
                      </div>
                    </div>
                  </SCard>
                  <SCard
                    title="Discounts"
                    subtitle="Allow hotels to fine tune their pricing strategies with a custom minimum discount"
                  >
                    <Discount
                      form={form}
                      mode={mode}
                      onDirty={() => setIsDirtyMove(true)}
                    />
                  </SCard>
                </>
              )}
            </Spinner>
          </Content>
        </SDetailLayout>
      )}
    </FormLegacyRenderProp>
  );
};
